import React, { useEffect, useState } from "react";
import "./reservationStatusModal.css";

// State machine values consumed by this modal.
// Owned and transitioned by Reservation.jsx; this component is presentational.
export const MODAL_STATE = Object.freeze({
  IDLE: "IDLE",
  SUBMITTING: "SUBMITTING",
  SENDING_EMAIL: "SENDING_EMAIL",
  EMAIL_SENT: "EMAIL_SENT",
  RESENDING: "RESENDING",
  EMAIL_RESENT: "EMAIL_RESENT",
  INITIAL_FAILURE: "INITIAL_FAILURE",
  RESEND_FAILED: "RESEND_FAILED",
});

const OWNER_PHONE = "777 606 447";
const OWNER_PHONE_TEL = "+420777606447";
const RESEND_COOLDOWN_MS = 30000;

const ReservationSummary = ({ summary }) => {
  if (!summary) return null;
  return (
    <ul className="rsmSummary">
      <li>
        <span>Barber</span>
        <strong>{summary.employeeName}</strong>
      </li>
      <li>
        <span>Datum</span>
        <strong>{summary.date}</strong>
      </li>
      <li>
        <span>Čas</span>
        <strong>{summary.time}</strong>
      </li>
      <li>
        <span>Služba</span>
        <strong>{summary.service}</strong>
      </li>
      {summary.additionalServices && (
        <li>
          <span>Extra služby</span>
          <strong>{summary.additionalServices}</strong>
        </li>
      )}
      <li>
        <span>Cena</span>
        <strong>{summary.totalPrice} Kč</strong>
      </li>
    </ul>
  );
};

const OwnerContact = ({ tone }) => (
  <div className={`rsmOwnerContact rsmOwnerContact--${tone}`}>
    <p className="rsmOwnerContactLabel">
      {tone === "strong"
        ? "Zavolej nebo napiš nám:"
        : "Pokud email stále nedorazil, kontaktuj nás přímo:"}
    </p>
    <div className="rsmOwnerContactActions">
      <a className="rsmOwnerCallBtn" href={`tel:${OWNER_PHONE_TEL}`}>
        Zavolat {OWNER_PHONE}
      </a>
      <a
        className="rsmOwnerSmsBtn"
        href={`sms:${OWNER_PHONE_TEL}`}
      >
        Poslat SMS
      </a>
    </div>
  </div>
);

const Spinner = () => <div className="rsmSpinner" aria-hidden="true" />;

export const ReservationStatusModal = ({
  state,
  onResend,
  onReset,
  reservationSummary,
  failureReason, // "SLOT_TAKEN" | "EMAIL_FAILED" | "VALIDATION" | undefined
}) => {
  // 30-second cooldown that disables the resend button after entering
  // EMAIL_SENT or EMAIL_RESENT, to discourage impatient duplicate sends.
  const [resendDisabledUntil, setResendDisabledUntil] = useState(0);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (state === MODAL_STATE.EMAIL_SENT || state === MODAL_STATE.EMAIL_RESENT) {
      setResendDisabledUntil(Date.now() + RESEND_COOLDOWN_MS);
    }
  }, [state]);

  useEffect(() => {
    if (resendDisabledUntil === 0) return;
    const tick = setInterval(() => {
      const next = Date.now();
      setNow(next);
      if (next >= resendDisabledUntil) {
        clearInterval(tick);
      }
    }, 250);
    return () => clearInterval(tick);
  }, [resendDisabledUntil]);

  if (state === MODAL_STATE.IDLE) return null;

  const cooldownRemaining = Math.max(0, resendDisabledUntil - now);
  const cooldownActive = cooldownRemaining > 0;
  const cooldownSeconds = Math.ceil(cooldownRemaining / 1000);

  const renderBody = () => {
    switch (state) {
      case MODAL_STATE.SUBMITTING:
        return (
          <div className="rsmCenteredState">
            <Spinner />
            <h2>Zpracováváme tvoji rezervaci…</h2>
            <p className="rsmHint">Chvíli to vydrž.</p>
          </div>
        );

      case MODAL_STATE.SENDING_EMAIL:
        return (
          <div className="rsmCenteredState">
            <Spinner />
            <h2>Odesíláme potvrzovací email…</h2>
            <p className="rsmHint">Za chvíli ti dorazí do schránky.</p>
          </div>
        );

      case MODAL_STATE.EMAIL_SENT:
        return (
          <>
            <div className="rsmIcon rsmIcon--info">✉</div>
            <h2>Zkontroluj svůj email</h2>
            <p className="rsmBody">
              Poslali jsme ti odkaz pro potvrzení rezervace. Klikni na něj
              v emailu — <strong>rezervace zatím není platná</strong> a stane
              se platnou až po kliknutí na odkaz.
            </p>
            <p className="rsmWarning">
              <strong>Neopouštěj prosím tuto stránku</strong>, dokud rezervaci
              nepotvrdíš. Pokud email nedorazí, můžeš ho odsud poslat znovu.
            </p>
            <ReservationSummary summary={reservationSummary} />
            <button
              type="button"
              className="rsmResendBtn"
              onClick={onResend}
              disabled={cooldownActive}
            >
              {cooldownActive
                ? `Email nepřišel? Zkus to znovu za ${cooldownSeconds}s`
                : "Email nepřišel? Odeslat znovu"}
            </button>
          </>
        );

      case MODAL_STATE.RESENDING:
        return (
          <div className="rsmCenteredState">
            <Spinner />
            <h2>Generujeme nový odkaz…</h2>
            <p className="rsmHint">Posíláme ti nový email s novým odkazem.</p>
          </div>
        );

      case MODAL_STATE.EMAIL_RESENT:
        return (
          <>
            <div className="rsmIcon rsmIcon--info">✉</div>
            <h2>Nový email byl odeslán</h2>
            <p className="rsmBody">
              Použij odkaz z <strong>nejnovějšího</strong> emailu —
              předchozí odkaz už nefunguje.
            </p>
            <p className="rsmWarning">
              Rezervace stále není platná, dokud ji nepotvrdíš kliknutím
              na odkaz. Neopouštěj prosím tuto stránku.
            </p>
            <ReservationSummary summary={reservationSummary} />
            <OwnerContact tone="soft" />
            <button
              type="button"
              className="rsmResendBtn"
              onClick={onResend}
              disabled={cooldownActive}
            >
              {cooldownActive
                ? `Odeslat znovu (${cooldownSeconds}s)`
                : "Odeslat znovu"}
            </button>
          </>
        );

      case MODAL_STATE.INITIAL_FAILURE: {
        const isSlotTaken = failureReason === "SLOT_TAKEN";
        return (
          <>
            <div className="rsmIcon rsmIcon--error">!</div>
            <h2>
              {isSlotTaken
                ? "Termín už není volný"
                : "Něco se pokazilo"}
            </h2>
            <p className="rsmBody">
              {isSlotTaken
                ? "Termín byl mezitím obsazen. Vyber prosím jiný čas."
                : "Nepodařilo se odeslat potvrzovací email. Zkus to prosím znovu, nebo nás kontaktuj přímo."}
            </p>
            {!isSlotTaken && <OwnerContact tone="soft" />}
            <button type="button" className="rsmResetBtn" onClick={onReset}>
              {isSlotTaken ? "Vybrat jiný termín" : "Vrátit se na rezervaci"}
            </button>
          </>
        );
      }

      case MODAL_STATE.RESEND_FAILED:
        return (
          <>
            <div className="rsmIcon rsmIcon--error">!</div>
            <h2>Nepodařilo se odeslat nový email</h2>
            <p className="rsmBody">
              Vypadá to, že máme problém s odesíláním emailů. Aby tvá rezervace
              nepropadla, prosíme, kontaktuj nás přímo — domluvíme termín
              telefonicky nebo SMS.
            </p>
            <OwnerContact tone="strong" />
            <button type="button" className="rsmResetBtn" onClick={onReset}>
              Vrátit se na rezervaci
            </button>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div
      className="rsmBackdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="rsmTitle"
    >
      <div className="rsmCard">
        <div id="rsmTitle" className="rsmContent">
          {renderBody()}
        </div>
      </div>
    </div>
  );
};
